import React, { useState } from 'react';
import { ArrowLeft, Users, Coins, ExternalLink, CheckCircle, Building, Calendar } from 'lucide-react';
import { ActionPanel } from './ActionPanel';
import { ActionDetailPanel } from './ActionDetailPanel';
import { RosterModal } from './RosterModal';
import { EmptyState } from './EmptyState';
import { useToast } from './Toast';
interface Site {
  id: string;
  name: string;
  issueType: string;
  affectedGuards: number;
  projectedCost: string;
  hasAction: boolean;
}
interface SiteListProps {
  provinceName: string;
  provinceId: string;
  areaName: string;
  areaId: string;
  onBack: () => void;
}
// Mock data for sites
const sitesByArea: Record<string, Site[]> = {
  'ct-cbd': [{
    id: 'woolworths-hq',
    name: 'Woolworths HQ',
    issueType: 'Projected Overtime',
    affectedGuards: 3,
    projectedCost: 'R12,500',
    hasAction: false
  }, {
    id: 'std-bank',
    name: 'Standard Bank Tower',
    issueType: 'Understaffed',
    affectedGuards: 2,
    projectedCost: 'R8,200',
    hasAction: true
  }],
  // Default fallback for other areas
  default: [{
    id: 'site-1',
    name: 'Generic Office Park',
    issueType: 'Projected Overtime',
    affectedGuards: 1,
    projectedCost: 'R4,500',
    hasAction: false
  }]
};
export function SiteList({
  provinceName,
  areaName,
  areaId,
  onBack
}: SiteListProps) {
  const {
    showToast
  } = useToast();
  const sites = sitesByArea[areaId] || sitesByArea.default;
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [viewingActionSite, setViewingActionSite] = useState<Site | null>(null);
  const [rosterSite, setRosterSite] = useState<Site | null>(null);
  const handleTakeAction = (site: Site) => {
    setSelectedSite(site);
  };
  const handleViewAction = (site: Site) => {
    setViewingActionSite(site);
  };
  const handleViewRoster = (site: Site) => {
    setRosterSite(site);
  };
  const handleClosePanel = () => {
    setSelectedSite(null);
  };
  const handleCloseDetailPanel = () => {
    setViewingActionSite(null);
  };
  const handleCloseRoster = () => {
    setRosterSite(null);
  };
  const handleSubmitAction = (data: any) => {
    console.log('Action created:', data);
    showToast('Action created successfully', 'success');
    handleClosePanel();
  };
  return <>
      <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-1068">
        {/* Header Section */}
        <div className="flex flex-col space-y-2" data-id="element-1069">
          <button onClick={onBack} className="group flex w-fit items-center text-sm font-medium text-[#5a7280] hover:text-[#062133] transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-[#062133]/5" aria-label="Back to area list" data-id="element-1070">
            <ArrowLeft className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-1" data-id="element-1071" />
            Back to {provinceName}
          </button>

          <div className="flex items-center justify-between" data-id="element-1072">
            <div data-id="element-1073">
              <h2 className="text-2xl font-bold text-[#062133]" data-id="element-1074">
                Flagged Sites in {areaName}
              </h2>
              <p className="text-[#5a7280]" data-id="element-1075">
                Review sites requiring attention and take action
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Table View (hidden on mobile) */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm" data-id="element-1076">
          <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-1077">
            <thead className="bg-[#fafaf9]" data-id="element-1078">
              <tr data-id="element-1079">
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1080">
                  Site Name
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1081">
                  Issue Type
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1082">
                  Affected Guards
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1083">
                  Projected Cost
                </th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1084">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-1085">
              {sites.length > 0 ? sites.map(site => <tr key={site.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => handleViewRoster(site)} data-id="element-1086">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1087">
                      <div className="text-sm font-semibold text-[#062133]" data-id="element-1088">
                        {site.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1089">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${site.issueType === 'Projected Overtime' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`} data-id="element-1090">
                        {site.issueType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1091">
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-1092">
                        <Users className="mr-1.5 h-4 w-4" data-id="element-1093" />
                        {site.affectedGuards} guards
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1094">
                      <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-1095">
                        <Coins className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1096" />
                        {site.projectedCost}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1097">
                      <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()} data-id="element-1098">
                        <button onClick={() => handleViewRoster(site)} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-1099">
                          <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-1100" />
                          View Roster
                        </button>
                        {site.hasAction ? <button onClick={() => handleViewAction(site)} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-1101">
                            <CheckCircle className="mr-1.5 h-3.5 w-3.5 text-emerald-500" data-id="element-1102" />
                            View Action
                          </button> : <button onClick={() => handleTakeAction(site)} className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-[#ff697f] hover:bg-[#e65a6f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff697f] min-h-[32px]" data-id="element-1103">
                            Take Action
                            <ExternalLink className="ml-1.5 h-3.5 w-3.5" data-id="element-1104" />
                          </button>}
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-1105">
                  <td colSpan={5} className="p-0 border-none" data-id="element-1106">
                    <EmptyState icon={Building} title="No flagged sites" description="All sites in this area are operating normally" data-id="element-1107" />
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View (hidden on desktop) */}
        <div className="md:hidden space-y-4" data-id="element-1108">
          {sites.length > 0 ? sites.map(site => <div key={site.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => handleViewRoster(site)} data-id="element-1109">
                <div className="flex justify-between items-start" data-id="element-1110">
                  <div data-id="element-1111">
                    <h3 className="text-lg font-semibold text-[#062133]" data-id="element-1112">
                      {site.name}
                    </h3>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${site.issueType === 'Projected Overtime' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`} data-id="element-1113">
                      {site.issueType}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-2 border-t border-b border-[#e5e5e5]" data-id="element-1114">
                  <div data-id="element-1115">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1116">
                      Affected Guards
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-1117">
                      <Users className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1118" />
                      {site.affectedGuards}
                    </div>
                  </div>
                  <div data-id="element-1119">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1120">
                      Projected Cost
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-1121">
                      <Coins className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1122" />
                      {site.projectedCost}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2" onClick={e => e.stopPropagation()} data-id="element-1123">
                  <button onClick={() => handleViewRoster(site)} className="flex-1 flex items-center justify-center px-4 py-3 border border-[#e5e5e5] text-sm font-medium rounded-xl text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[48px]" data-id="element-1124">
                    <Calendar className="mr-2 h-4 w-4" data-id="element-1125" />
                    View Roster
                  </button>
                  <button onClick={() => site.hasAction ? handleViewAction(site) : handleTakeAction(site)} className={`flex-1 flex items-center justify-center px-4 py-3 border text-sm font-medium rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[48px] ${site.hasAction ? 'border-[#e5e5e5] text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:ring-[#062133]' : 'border-transparent text-white bg-[#ff697f] hover:bg-[#e65a6f] focus:ring-[#ff697f]'}`} data-id="element-1126">
                    {site.hasAction ? <>
                        <CheckCircle className="mr-2 h-4 w-4 text-emerald-500" data-id="element-1127" />
                        View Action
                      </> : <>
                        Take Action
                        <ExternalLink className="ml-2 h-4 w-4" data-id="element-1128" />
                      </>}
                  </button>
                </div>
              </div>) : <EmptyState icon={Building} title="No flagged sites" description="All sites in this area are operating normally" data-id="element-1129" />}
        </div>
      </div>

      {/* Action Panel (Create) */}
      <ActionPanel isOpen={selectedSite !== null} siteName={selectedSite?.name || ''} onClose={handleClosePanel} onSubmit={handleSubmitAction} data-id="element-1130" />

      {/* Action Detail Panel (View) */}
      <ActionDetailPanel isOpen={viewingActionSite !== null} siteName={viewingActionSite?.name || ''} onClose={handleCloseDetailPanel} data-id="element-1131" />

      {/* Roster Modal */}
      <RosterModal isOpen={rosterSite !== null} siteName={rosterSite?.name || ''} siteId={rosterSite?.id || ''} onClose={handleCloseRoster} onTakeAction={() => {
      if (rosterSite) {
        handleTakeAction(rosterSite);
      }
    }} data-id="element-1132" />
    </>;
}